import moment from 'moment';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import iconBaths from '../../../images/icons/icon-bath.svg';
import DollarIcon from '../../../images/icons/icon-Dollar.svg';
import iconBeds from '../../../images/icons/icon-beds.svg';
import iconBack from '../../../images/icons/icon-chevron-right.svg';
import iconClose from '../../../images/icons/icon-close.svg';
import iconSqFeet from '../../../images/icons/icon-square-feet.svg';
import { IRootState } from '../../../redux/rootReducer';
import styles from './BookAppointment.module.css';
import Confirm from './Confirm/Confirm';
import ReserveCalendar from './ReserveCalendar/ReserveCalendar';
import TimeSlots, { LockerInfo } from './TimeSlots/TimeSlots';
import useGeolocation from '../../../hooks/useGeoLocation';
import Popup from '../../common/popup/Popup';

interface BookAppointmentProps {
    setOpen: Dispatch<SetStateAction<boolean>>;
    appointmentDate: string;
    appoinmentStartTime: string;
    appointmentCloseTime: string;
    showPrice?: boolean
}

export interface SlotsProps {
    label: string;
    value: string;
    isDisabled?: boolean;
}

type LocationState = { appointmentId: string };

const BookAppointment = ({ setOpen, appoinmentStartTime, appointmentDate, appointmentCloseTime, showPrice = true }: BookAppointmentProps) => {
    const { appointmentId } = (useLocation().state || '') as LocationState;

    const search = useLocation().search;

    const [showCalendar, setShowCalendar] = useState(true);
    const [showTime, setShowTime] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [timings, setTimings] = useState({ startTime: '', endTime: '' });
    const [availabilityId, setAvailabilityId] = useState([]);
    const [availableIds, setAvailableIds] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [slots, setSlots] = useState<SlotsProps[]>([]);
    const [selectedSlots, setSelectedSlots] = useState('');
    const [lockerInfo, setLockerInfo] = useState<LockerInfo[]>([]);
    const [showCustomModal, setShowCustomModal] = useState(false);

    const unit = useSelector((state: IRootState) => state.unitReducer.unit.data.units);

    const appointmentData = useSelector((state: IRootState) => state?.appointmentReducer?.appointment?.data);

    useEffect(() => {
        if (search) {
            // let [modifiedAppointmentDate] = search.replace(/\?/g, '').replace(/&/g, '').split('_');
            const searchParams = new URLSearchParams(search);
            const oldBookedDate = searchParams.get('oldBookedDate') || '';
            const modifiedAppointmentDate = moment(oldBookedDate).format('YYYY-MM-DD');
            setSelectedDate(moment(modifiedAppointmentDate).format());
        }
    }, [search]);

    const handleBack = () => {
        if (showTime) {
            setShowTime(false);
            setShowCalendar(true);
            setOpen(false);
            setSelectedDate(selectedDate);
            setSelectedSlots(selectedSlots);
        }
        if (showConfirm) {
            setShowConfirm(false);
            setShowTime(true);
            setShowCalendar(true);
            setSelectedDate(selectedDate);
            setSelectedSlots(selectedSlots);
        }
    };

    const getPlatform = (): "android" | "ios" | "default" => {
        const userAgentData = (navigator as any).userAgentData;

        // Check modern userAgentData if available
        if (userAgentData?.platform) {
            const platform = userAgentData.platform.toLowerCase();
            if (platform.includes("android")) return "android";
            if (platform.includes("iphone") || platform.includes("ipad") || platform.includes("ios")) return "ios";
        }

        // Fallback to userAgent (works for most browsers)
        const userAgent = navigator.userAgent.toLowerCase();
        if (/android/.test(userAgent)) return "android";
        if (/iphone|ipad|ipod/.test(userAgent)) return "ios";

        // Additional check for iPadOS, but only if OS is Mac AND browser is Safari (to avoid Ubuntu false positive)
        if (
            navigator.platform === "MacIntel" &&
            navigator.maxTouchPoints > 1 &&
            /safari/.test(userAgent) &&
            !/chrome|firefox|edg|opr/.test(userAgent) // Ensure it's not Ubuntu
        ) {
            return "ios";
        }

        return "default"; // If nothing matches
    };


    const getBrowserName = (): string => {
        const userAgent = navigator.userAgent.toLowerCase();
        const userAgentData = (navigator as any).userAgentData;

        // iOS Browsers
        if (userAgent.includes("crios")) return "Chrome (iOS)";
        if (userAgent.includes("fxios")) return "Firefox (iOS)";
        if (userAgent.includes("edgios")) return "Edge (iOS)";

        // Chromium-based browsers
        if (userAgent.includes("duckduckgo")) return "DuckDuckGo";
        if (userAgent.includes("vivaldi")) return "Vivaldi";
        if (userAgent.includes("samsungbrowser")) return "Samsung Internet";
        if (userAgent.includes("yabrowser")) return "Yandex Browser";
        if (userAgent.includes("ucbrowser")) return "UC Browser";

        // More reliable Brave detection
        const isBrave =
            (navigator as any).brave?.isBrave || // Check if Brave explicitly exposes itself
            (userAgentData?.brands?.some((brand: { brand: string }) => brand.brand.toLowerCase() === "brave")); // Check using userAgentData

        if (isBrave) return "Brave";

        // Standard Browsers
        if (userAgent.includes("opr") || userAgent.includes("opera")) return "Opera";
        if (userAgent.includes("chrome") && !userAgent.includes("edg") && !userAgent.includes("opr")) return "Chrome";
        if (userAgent.includes("firefox")) return "Firefox";
        if (userAgent.includes("safari") && !userAgent.includes("chrome")) return "Safari";
        if (userAgent.includes("edg")) return "Edge";
        if (userAgent.includes("trident") || userAgent.includes("msie")) return "Internet Explorer";

        return "Unknown";
    };

    const CustomModalBody = () => {
        // const platform: "android" | "ios" | "default" = getPlatform();
        const platform: "android" | "ios" | "default" = getPlatform();
        const browser: string = getBrowserName();

        const instructions: Record<string, { message: string; }> = {
            android: {
                message: `To enable location:\n<span>1️⃣ Open ${browser}.</span><span>2️⃣ Go to Settings > Site Settings.</span><span>3️⃣ Enable location for this website.</span>`,
            },
            ios: {
                message: `To enable location:\n<span>1️⃣ Open Settings.</span><span>2️⃣ Go to Privacy & Security > Location Services.</span><span>3️⃣ Enable location for ${browser}.</span>`
            },
            default: {
                message: "Please enable location permissions in your browser settings.",
            }
        };

        const { message } = instructions[platform] || instructions.default;

        return (<div className={styles.modalBodyContainer}>
            <div className={`${styles.modalHeader} allowSVG`}>
                <h3 className={styles.modalH3}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C7.6 2 4 5.6 4 10C4 15.4 11 21.5 11.3 21.8C11.5 21.9 11.8 22 12 22C12.2 22 12.5 21.9 12.7 21.8C13 21.5 20 15.4 20 10C20 5.6 16.4 2 12 2ZM12 19.7C9.9 17.7 6 13.4 6 10C6 6.7 8.7 4 12 4C15.3 4 18 6.7 18 10C18 13.3 14.1 17.7 12 19.7ZM12 6C9.8 6 8 7.8 8 10C8 12.2 9.8 14 12 14C14.2 14 16 12.2 16 10C16 7.8 14.2 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12Z" fill="black" />
                    </svg>
                    <span>Enable Location Access</span>
                </h3>
            </div>
            <div className={styles.modalBodyInnerContainer}>
                <p className={styles.preWrappedMessage} dangerouslySetInnerHTML={{ __html: message }} ></p>
            </div>

            <div className={styles.modalFooter}>
                <button className={styles.captureBtn} onClick={() => setShowCustomModal(false)}>Okay</button>
            </div>
        </div>)
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.flex}>
                    {!showCalendar && (
                        <div className={styles.back} onClick={handleBack}>
                            <img src={iconBack} alt="back" />
                        </div>
                    )}
                    <div className={`${styles.headerContent} ${showConfirm ? styles.withConfirm : ''}`}>
                        <div className={styles.wrapper}>
                            <h3 className={styles.headerTitle}>
                                {unit?.buildingName ? `${unit?.buildingName} - ` : ''}
                                {unit?.unitYardiId ? unit?.unitYardiId : unit?.name} {unit?.nickName && <span> "{unit?.nickName}" </span>}
                            </h3>
                        </div>
                        <div className={styles.facilities}>
                            <span className={styles.facility}>
                                <img src={iconSqFeet} alt="" />
                                {unit?.size} Sq Ft
                            </span>
                            <span className={styles.facility}>
                                <img src={iconBeds} alt="" />

                                <span>
                                    {+unit?.rooms ? unit?.rooms : ''} {!+unit?.rooms ? 'Studio' : +unit.rooms === 1 ? 'Bed' : 'Beds'}
                                </span>
                            </span>
                            <span className={styles.facility}>
                                <img src={iconBaths} alt="" />
                                {(+unit?.baths || +unit?.baths === 0) && (
                                    <span>{`${+unit?.baths} ${+unit?.baths === 1 ? 'Bath' : 'Baths'}`}</span>
                                )}
                            </span>
                            {!!showPrice && (
                                <span className={styles.facility}>
                                    <img src={DollarIcon} alt="" />
                                    {unit?.price ? unit?.price : '0'}*
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <span className={styles.closeBtn} onClick={() => setOpen(false)}>
                    <img src={iconClose} alt="Close" />
                </span>
            </div>

            {showCalendar && (
                <ReserveCalendar
                    selectedDate={selectedDate}
                    setShowCalendar={setShowCalendar}
                    setShowTime={setShowTime}
                    setAvailabilityId={setAvailabilityId}
                    setSelectedDate={setSelectedDate}
                    availabilities={unit?.availabilities}
                    setShowConfirm={setShowConfirm}
                    slots={slots}
                    setSelectedSlots={setSelectedSlots}
                    selectedSlots={selectedSlots}
                    availabilityId={availabilityId}
                    setAvailableIds={setAvailableIds}
                    appoinmentStartTime={appoinmentStartTime}
                    appointmentCloseTime={appointmentCloseTime}
                    appointmentDate={appointmentDate}
                />
            )}
            {showTime && (
                <TimeSlots
                    unit={unit}
                    availabilityId={availabilityId}
                    timings={timings}
                    setTimings={setTimings}
                    selectedDate={selectedDate}
                    setShowTime={setShowTime}
                    setShowConfirm={setShowConfirm}
                    setAvailableIds={setAvailableIds}
                    setSlots={setSlots}
                    setLockerInfo={setLockerInfo}
                    lockerInfo={lockerInfo}
                />
            )}
            {showConfirm && (
                <Confirm
                    selectedSlots={selectedSlots}
                    timings={timings}
                    availabilities={availableIds}
                    selectedDate={selectedDate}
                    unit={unit}
                    lockerInfo={lockerInfo}
                    appointmentId={appointmentId}
                    setShowModal={setShowConfirm}
                    setShowCustomModal={setShowCustomModal}
                />
            )}

            {showCustomModal && (
                <Popup setOpen={setShowCustomModal} className="custom-webcam-modal" open={showCustomModal} bodyData={<CustomModalBody />} />
            )}
        </div>
    );
};

export default BookAppointment;
